/* global algoliaSearchLocalizedData */
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import instantsearch from 'instantsearch.js';
import {
	searchBox,
	stats,
	hits,
	refinementList,
	configure,
	clearRefinements,
	pagination,
} from 'instantsearch.js/es/widgets';
import { connectPagination } from 'instantsearch.js/es/connectors';
import { history } from 'instantsearch.js/es/lib/routers';

import { createDropdown } from './Dropdown';

// The block data is only localized server-side when the Algolia block actually
// has data (e.g. not in the editor). Default it here so the reads below can
// safely access window.algoliaSearchBlockData.* — otherwise this threw
// "Cannot read properties of undefined (reading 'fallbackImage')". The real
// localized data, when present, is kept.
window.algoliaSearchBlockData = window.algoliaSearchBlockData || {
	fallbackImage: '',
	fallbackImageSrcset: '',
	defaultSections: [],
};

// Set index name from localized data
if (typeof algoliaSearchBlockData !== 'undefined') {
	algoliaSearchLocalizedData.indexName =
		algoliaSearchBlockData['era_algolia_block_index'];
}

const searchClient = algoliasearch(
	algoliaSearchLocalizedData.appID,
	algoliaSearchLocalizedData.apiKey
);

const indexName = `${algoliaSearchLocalizedData.environment}_${algoliaSearchLocalizedData.indexName}`;

/**
 * Refinements the block (or the archive it's on) preselects, as
 * [attribute, value] pairs.
 */
const preselectedRefinements = [
	...[].concat(algoliaSearchBlockData['era_algolia_block_preselected_attributes'] || [])
		.filter((attribute) => typeof attribute === 'string' && attribute.includes(':'))
		.map((attribute) => {
			const [name, ...value] = attribute.split(':');
			return [name, value.join(':')];
		}),
	...[].concat(algoliaSearchBlockData['era_algolia_block_preselected_post_types'] || [])
		.filter(Boolean)
		.map((postType) => ['post_type', postType]),
];

// ----------------------
// Routing
// ----------------------
/**
 * Listings rendered server-side (see Server_Hits) keep the page number in the
 * path, e.g. /blog/page/2/, so URLs match the crawlable pagination links.
 */
const isPagedListing = !!document.querySelector('#pagination[data-paged-listing]');
const PAGE_PATH = /\/page\/(\d+)\/?$/;

/**
 * Leave preselected refinements out of the URL: they are applied on every
 * load anyway, so the URL only changes once the user searches or filters.
 */
const withoutPreselectedRefinements = (refinementList = {}) => {
	const result = { ...refinementList };
	preselectedRefinements.forEach(([attribute, value]) => {
		if (!Array.isArray(result[attribute])) {
			return;
		}
		result[attribute] = result[attribute].filter((refined) => refined !== value);
		if (!result[attribute].length) {
			delete result[attribute];
		}
	});
	return result;
};

const router = history({
	cleanUrlOnDispose: false,
	createURL({ qsModule, routeState, location }) {
		const { [indexName]: indexState = {}, ...otherIndices } = routeState;
		const { page, refinementList, ...urlIndexState } = indexState;

		const urlRefinementList = withoutPreselectedRefinements(refinementList);
		if (Object.keys(urlRefinementList).length) {
			urlIndexState.refinementList = urlRefinementList;
		}

		let pathname = location.pathname;
		if (isPagedListing) {
			pathname = pathname.replace(PAGE_PATH, '/').replace(/\/?$/, '/');
			if (page > 1) {
				pathname += `page/${page}/`;
			}
		} else if (page) {
			urlIndexState.page = page;
		}

		const urlState = { ...otherIndices };
		if (Object.keys(urlIndexState).length) {
			urlState[indexName] = urlIndexState;
		}

		const queryString = qsModule.stringify(urlState);

		return `${location.origin}${pathname}${queryString ? `?${queryString}` : ''}${location.hash}`;
	},
	parseURL({ qsModule, location }) {
		const routeState = qsModule.parse(location.search.slice(1), { arrayLimit: 99 });
		const indexState = { ...routeState[indexName] };

		// Put back the preselected refinements createURL leaves out, so they
		// survive back/forward navigation too.
		if (preselectedRefinements.length) {
			const refinementList = { ...indexState.refinementList };
			preselectedRefinements.forEach(([attribute, value]) => {
				const values = [].concat(refinementList[attribute] || []);
				if (!values.includes(value)) {
					values.push(value);
				}
				refinementList[attribute] = values;
			});
			indexState.refinementList = refinementList;
		}

		const pageMatch = location.pathname.match(PAGE_PATH);
		if (isPagedListing && pageMatch) {
			indexState.page = Number(pageMatch[1]);
		}

		if (Object.keys(indexState).length) {
			routeState[indexName] = indexState;
		}

		return routeState;
	},
});

const search = instantsearch({
	indexName,
	searchClient,
	stalledSearchDelay: 0,
	routing: { router },
});

// Fetch Manual Facet Terms from Algolia Settings page
// Will only pull the terms selected in the settings page.
const manualFacetSlugs = (window.algoliaSearchLocalizedData.manualFacetSlugs || []).map(
	String
);

/**
 * Facet behavior limits.
 * - Fetch: how many facet values Algolia should return.
 * - Limit: how many are shown before "show more".
 */
const FACET_FETCH_LIMIT = 500;
const FACET_VISIBLE_LIMIT = 10;
const FACET_SHOW_MORE_LIMIT = 500;

/**
 * Track latest selected facet value per attribute to keep it pinned to top.
 */
const lastSelectedFacetValueByAttribute = {};
let pendingScrollToAlgoliaTop = false;
let pendingScrollBehavior = 'smooth';
let hasRefinementSignature = false;
let previousRefinementSignature = '';
const SCROLL_TOP_EXTRA_OFFSET = 16;

const getCssVarPx = (variableName) => {
	const raw = window.getComputedStyle(document.documentElement).getPropertyValue(variableName);
	const value = Number.parseFloat(raw);
	return Number.isFinite(value) ? value : 0;
};

const getHeaderOffset = () => {
	const header =
		document.getElementById('siteHeader') ||
		document.querySelector('.site-header');

	let headerHeight = 0;
	if (header) {
		const styles = window.getComputedStyle(header);
		const isPinned = styles.position === 'fixed' || styles.position === 'sticky';
		if (isPinned) {
			headerHeight = Math.max(
				header.getBoundingClientRect().height,
				Number.parseFloat(styles.height) || 0
			);
		}
	}

	if (!headerHeight) {
		headerHeight = getCssVarPx('--header-height') || (window.innerWidth >= 1024 ? 118.021 : 71.9965);
	}

	const adminBar = document.getElementById('wpadminbar');
	let adminBarHeight = 0;
	if (adminBar) {
		const adminBarStyles = window.getComputedStyle(adminBar);
		if (adminBarStyles.position === 'fixed') {
			adminBarHeight = adminBar.getBoundingClientRect().height;
		}
	}

	return Math.ceil(headerHeight + adminBarHeight + SCROLL_TOP_EXTRA_OFFSET);
};

const scrollAlgoliaBlockIntoView = (behavior = 'smooth') => {
	const algoliaBlock = document.getElementById('r-wp-algolia-container');
	if (!algoliaBlock) {
		return;
	}

	const headerOffset = getHeaderOffset();
	const top = Math.max(
		0,
		Math.round(algoliaBlock.getBoundingClientRect().top + window.pageYOffset - headerOffset)
	);
	window.scrollTo({ top, left: 0, behavior });
};

const queueScrollToAlgoliaTop = (behavior = 'smooth') => {
	pendingScrollBehavior = behavior;
	pendingScrollToAlgoliaTop = true;
};

const flushQueuedScrollToAlgoliaTop = () => {
	const activeElement = document.activeElement;
	if (activeElement instanceof HTMLElement && activeElement !== document.body) {
		activeElement.blur();
	}

	scrollAlgoliaBlockIntoView(pendingScrollBehavior);
	window.setTimeout(() => {
		scrollAlgoliaBlockIntoView('auto');
	}, 120);
};

const getFacetShowMoreLabel = (isShowingMore) => {
	const currentLang = algoliaSearchLocalizedData.currentLang || 'default';
	const localeLang = (algoliaSearchLocalizedData.locale || '').split('-')[0].toLowerCase();
	const shortLang = currentLang.split(/[-_]/)[0].toLowerCase();
	const languageCandidates = Array.from(
		new Set([currentLang, shortLang, localeLang, 'default'].filter(Boolean))
	);
	const facetTranslations = algoliaSearchLocalizedData.translations?.filters || {};
	const key = isShowingMore ? '__show_less' : '__show_more';
	let fromFacetSettings = '';
	for (const candidate of languageCandidates) {
		const value = facetTranslations?.[key]?.[candidate];
		if (value) {
			fromFacetSettings = value;
			break;
		}
	}

	if (fromFacetSettings) {
		return fromFacetSettings;
	}

	return isShowingMore
		? algoliaSearchLocalizedData.translations.showLessFacets || 'Show less'
		: algoliaSearchLocalizedData.translations.showMoreFacets || 'Show more';
};

const applyFacetShowMoreTranslationsInDom = () => {
	const translatedShowMore = getFacetShowMoreLabel(false);
	const translatedShowLess = getFacetShowMoreLabel(true);
	const showMoreRegex = /^show\s+more$/i;
	const showLessRegex = /^show\s+less$/i;

	document.querySelectorAll('.ais-RefinementList-showMore').forEach((button) => {
		const currentText = (button.textContent || '').trim();
		if (showMoreRegex.test(currentText)) {
			button.textContent = translatedShowMore;
		}
		if (showLessRegex.test(currentText)) {
			button.textContent = translatedShowLess;
		}
	});
};

const normalizeFacetRefinements = (refinements) => {
	const result = {};
	Object.keys(refinements || {})
		.sort()
		.forEach((attribute) => {
			const rawValues = refinements[attribute];
			const values = Array.isArray(rawValues)
				? rawValues
				: Object.keys(rawValues || {});

			result[attribute] = Array.from(new Set(values.map(String))).sort();
		});
	return result;
};

const getRefinementSignature = (state) =>
	JSON.stringify({
		disjunctive: normalizeFacetRefinements(state?.disjunctiveFacetsRefinements || {}),
		conjunctive: normalizeFacetRefinements(state?.facetsRefinements || {}),
		hierarchical: normalizeFacetRefinements(state?.hierarchicalFacetsRefinements || {}),
	});

/**
 * Algolia fallback image
 */
const fallbackImage = window.algoliaSearchBlockData.fallbackImage || '';
// const fallbackImageSrcset = window.algoliaSearchBlockData.fallbackImageSrcset || '';
/*console.log( 'fallbackImage:', fallbackImage );*/

/**
 * Algolia supergraphic logic
 */

const _norm = (v) => (v ?? '').toString().trim().toLowerCase();
const _mapStyleClass = (style) => {
	switch (_norm(style)) {
		case 'supergraphic':
		case 'primary':
		case 'sg':
		case 'sg-primary': return 'has-supergraphic';
		case 'secondary':
		case 'supergraphic-secondary':
		case 'sg-secondary': return 'has-supergraphic-secondary';
		case 'tertiary':
		case 'supergraphic-tertiary':
		case 'sg-tertiary': return 'has-supergraphic-tertiary';
		default: return '';
	}
};
const _mapBgClass = (bg) => {
	switch (_norm(bg)) {
		case 'brand':         return 'has-brand-background-color';
		case 'electric-blue':
		case 'electricblue':  return 'has-electric-blue-background-color';
		case 'green':         return 'has-green-background-color';
		case 'green-pale':
		case 'greenpale':     return 'has-green-pale-background-color';
		case 'peach':         return 'has-peach-background-color';
		default: return '';
	}
};

/**
 * ::DEBUGGING::
 */
console.log('INITIAL LOCALIZED DATA: ', algoliaSearchLocalizedData);
console.log('BLOCK DATA: ', algoliaSearchBlockData);
console.log( 'DEFAULTVIEW: ' , window.algoliaSearchBlockData.defaultSections );
const masterDefaultViewPostTypes = window.algoliaSearchBlockData.defaultSections .flatMap( s => s.masterDefaultPosts );
console.log( 'MASTER DEFAULT VIEW : ', masterDefaultViewPostTypes );
console.log( 'MASTER DEFAULT VIEW NEWSLETTER LINK', algoliaSearchBlockData.newsLetterLinkMaster);


// ----------------------
// Initial Results Section
// ----------------------
async function fetchInitialData() {
	const cfg = window.algoliaSearchBlockData.defaultSections || [];

	if (cfg.length > 0) {
		/*const queries = cfg.map(section => ({
			indexName,
			query: '',
			params: {
				hitsPerPage: section.hitsPerPage,
				filters: `post_type:${section.postType}`,
			},
		}));*/

		const queries = cfg.map((section, index) => ({
			indexName,
			query: '',
			params: {
				hitsPerPage: section.hitsPerPage,
				filters: `post_type:${
					section.defaultViewSelected === 'default' &&
					Array.isArray(section.masterDefaultPosts) &&
					section.masterDefaultPosts[index]
						? section.masterDefaultPosts[index]
						: section.postType
				}`,
			},
		}));

		const results = await searchClient.search(queries);

		const sections = cfg.map((section, i) => ({
			title: section.title,
			paragraph: section.paragraph,
			link: section.link,
			linkText: section.linkText,
			items:
				section.defaultViewSelected === 'manual' &&
				Array.isArray(section.manualPosts) &&
				section.manualPosts.length
					? section.manualPosts
					: section.defaultViewSelected === 'terms' &&
					Array.isArray(section.termPosts) &&
					section.termPosts.length
						? section.termPosts
						: results.results[i].hits,
		}));

		// --- NEW: sort each “default” section by publish_date desc ---
		sections.forEach(section => {
			if (section.defaultViewSelected === 'default') {
				section.items.sort((a, b) => {
					// ensure we have valid dates
					const da = new Date(a.publish_date).getTime() || 0;
					const db = new Date(b.publish_date).getTime() || 0;
					return db - da; // descending
				});
			}
		});

		renderInitialResults(sections);
		return;
	}

	const results = await searchClient.search([
		{
			indexName,
			query: '',
			params: { hitsPerPage: 3, filters: 'post_type:post' },
		},
		{
			indexName,
			query: '',
			params: { hitsPerPage: 3, filters: 'post_type:events' },
		},
		{
			indexName,
			query: '',
			params: { hitsPerPage: 3, filters: 'post_type:guides' },
		},
	]);

	renderInitialResults(results);
	console.log('ASYNC Results :', results);
}


// ----------------------
// Dynamic Facet Mounting
// ----------------------
const rawFacets = algoliaSearchBlockData.era_algolia_block_visible_facets || [];
const visibleFacets = rawFacets.map(f => (f === 'manual_facet' ? 'global_solutions' : f));
console.log('Selected facets for algolia block:', visibleFacets);

/**
 * Ensure Algolia sends enough facet values so refined values can be surfaced
 * even when they are not in the default top 10.
 */
search.addWidget(
	configure({
		maxValuesPerFacet: FACET_FETCH_LIMIT,
	})
);

const getRefinedValuesForAttribute = (attribute) => {
	if (!search.helper || !search.helper.state) {
		return [];
	}

	const disjunctive = search.helper.state.disjunctiveFacetsRefinements?.[attribute] || [];
	const conjunctiveRaw = search.helper.state.facetsRefinements?.[attribute] || [];
	const conjunctive = Array.isArray(conjunctiveRaw)
		? conjunctiveRaw
		: Object.keys(conjunctiveRaw);

	return [...new Set([...disjunctive, ...conjunctive].map((value) => String(value)))];
};

const ensureRefinedFacetItemsArePresent = (items, attribute) => {
	const refinedValues = getRefinedValuesForAttribute(attribute);
	if (!refinedValues.length) {
		return items;
	}

	const existingByValue = new Map(
		items.map((item) => [String(item.value), item])
	);

	const injectedRefined = refinedValues
		.filter((value) => !existingByValue.has(value))
		.map((value) => ({
			value,
			label: value,
			highlighted: value,
			count: 0,
			isRefined: true,
		}));

	// Add missing refined values first so they are guaranteed to render.
	const merged = [...injectedRefined, ...items];

	// Enforce refined flag from helper state for consistency.
	const refinedSet = new Set(refinedValues);
	return merged.map((item) => ({
		...item,
		isRefined: refinedSet.has(String(item.value)),
	}));
};

const moveSelectedFacetToTop = (items, attribute) => {
	const refinedValues = new Set(getRefinedValuesForAttribute(attribute));
	if (!refinedValues.size) {
		return items;
	}

	const selectedValue = String(lastSelectedFacetValueByAttribute[attribute] || '');
	const ordered = [...items];
	const top = [];

	if (selectedValue && refinedValues.has(selectedValue)) {
		const selectedIndex = ordered.findIndex(
			(item) => String(item.value) === selectedValue
		);

		if (selectedIndex > -1) {
			top.push(ordered[selectedIndex]);
			ordered.splice(selectedIndex, 1);
		}
	}

	const refined = ordered.filter((item) => refinedValues.has(String(item.value)));
	const unrefined = ordered.filter((item) => !refinedValues.has(String(item.value)));

	return [
		...top,
		...refined.filter((item) => String(item.value) !== selectedValue),
		...unrefined,
	];
};

rawFacets.forEach((facetSlug) => {
	const attribute = facetSlug;
	// const isManual = facetSlug === 'global_solutions';
	const hyph = facetSlug.replace(/_/g, '-');

	let desktopSelector, mobileSelector;
	if (facetSlug === 'post_type') {
		desktopSelector = '#post-type';
		mobileSelector = '#facet-post-type-mobile';
	}
	// else if (isManual) {
	// 	desktopSelector = '#facet-global_solutions';
	// 	mobileSelector = '#facet-global-solutions-mobile';
	// }
	else {
		desktopSelector = `#facet-${facetSlug}`;
		mobileSelector = `#facet-${hyph}-mobile`;
	}

		[desktopSelector, mobileSelector].forEach((container) => {
			const widgetOpts = {
				container,
				attribute,
				limit: FACET_VISIBLE_LIMIT,
				showMore: true,
				showMoreLimit: FACET_SHOW_MORE_LIMIT,
				sortBy: (a, b) => {
					if (facetSlug !== 'post_type') {
						return 0;
				}
				const aOrder = algoliaSearchBlockData.era_algolia_block_post_type_facet_order.find(
					(postType) => postType.name === a.name
				);
				let aOrderVal = typeof aOrder !== 'undefined' ? aOrder.order : 999;
				const bOrder = algoliaSearchBlockData.era_algolia_block_post_type_facet_order.find(
					(postType) => postType.name === b.name
				);
				let bOrderVal = typeof bOrder !== 'undefined' ? bOrder.order : 999;
				if (aOrderVal < bOrderVal) {
					return -1;
				}
				return 1;
				},
					templates: {
					showMoreText({ isShowingMore }, { html }) {
						return html`${getFacetShowMoreLabel(isShowingMore)}`;
					},
					item(item, {html}){

						const { label, isRefined, value } = item;
						return html`<label class="facet-item">
            <input
	              type="checkbox"
	              class="ais-RefinementList-checkbox"
	              data-algolia-facet-checkbox="1"
	              data-algolia-facet-attribute="${attribute}"
	              value="${value}"
	              checked=${isRefined}
	            />
						<span class="facet-item__label ais-RefinementList-labelText">${getAttributeLabel(label, facetSlug)}</span>
	          </label>
	        `},
				},
		};



		if (facetSlug === 'manual_facet' ) {
			widgetOpts.transformItems = (items) =>
				items.filter((item) => manualFacetSlugs.includes(item.value));
			widgetOpts.sortBy = (a, b) =>
				manualFacetSlugs.indexOf(a.value) - manualFacetSlugs.indexOf(b.value);
		}

		if (facetSlug === 'post_type') {
			const visiblePostTypeOrder =
				algoliaSearchBlockData.era_algolia_block_post_type_facet_order || [];

				if (visiblePostTypeOrder.length) {
					widgetOpts.transformItems = (items) =>
						items.filter((item) => {
							return Boolean(
								visiblePostTypeOrder.find((postType) => postType.name === item.value)
							);
						});
				}
				widgetOpts.limit = 20;
				widgetOpts.sortBy = (a, b) => {
				const aEntry = visiblePostTypeOrder.find((pt) => pt.name === a.name);
				const bEntry = visiblePostTypeOrder.find((pt) => pt.name === b.name);

				const aOrderVal = aEntry?.order ?? 999;
				const bOrderVal = bEntry?.order ?? 999;
				return aOrderVal - bOrderVal;
			};
		}

			const baseTransform = widgetOpts.transformItems;
			widgetOpts.transformItems = (items) => {
				const processed = baseTransform ? baseTransform(items) : items;
				const withRefinedPresent = ensureRefinedFacetItemsArePresent(processed, attribute);
				return moveSelectedFacetToTop(withRefinedPresent, attribute);
			};

		search.addWidget(refinementList(widgetOpts));
	});
});

function getAttributeLabel(label, facet_slug) {
	if (
		facet_slug === 'post_type' &&
		algoliaSearchLocalizedData.translations.post_types &&
		algoliaSearchLocalizedData.translations.post_types.hasOwnProperty(label)
	) {
		const postTypeTranslations =
			algoliaSearchLocalizedData.translations.post_types[label] || {};
		return (
			postTypeTranslations[algoliaSearchLocalizedData.currentLang] ||
			postTypeTranslations.default ||
			label
		);
	}

	return label;
}

/**
 * Pagination
 */
const customPagination = connectPagination((renderOptions, isFirstRender) => {
	const {
		pages,
		currentRefinement,
		nbPages,
		refine,
		createURL,
		widgetParams: {container, ariaControlsId}
	} = renderOptions;

	// Keep the server-rendered pagination until there are results to replace it.
	if (isFirstRender) {
		return;
	}

	const prevDisabled = currentRefinement === 0;
	const nextDisabled = currentRefinement === nbPages - 1;

	// Real links, so the pages are crawlable; same markup as Server_Hits.
	const pageLink = (pageIndex, disabled, className, label, isCurrent = false) => {
		if (disabled) {
			return `<span class="${className} swiper-button-disabled" aria-hidden="true"></span>`;
		}

		return `<a class="${className}" href="${createURL(pageIndex)}" aria-label="${label}" aria-controls="${ariaControlsId}" data-page="${pageIndex}"${isCurrent ? ' aria-current="page"' : ''}></a>`;
	};

	container.innerHTML = `
    <nav class="swiper-controls-wrapper" aria-label="Pagination">
      ${pageLink(currentRefinement - 1, prevDisabled, 'swiper-button-prev', 'Go to previous page')}
      <div class="swiper-pagination swiper-pagination-bullets swiper-pagination-horizontal">
        ${pages
		.map(pageIndex => {
			const isActive = pageIndex === currentRefinement;
			return pageLink(
				pageIndex,
				false,
				`swiper-pagination-bullet${isActive ? ' swiper-pagination-bullet-active' : ''}`,
				`Go to page ${pageIndex + 1}`,
				isActive
			);
		})
		.join('\n')}
      </div>
      ${pageLink(currentRefinement + 1, nextDisabled, 'swiper-button-next', 'Go to next page')}
    </nav>
  `;

	// Page in place; modified clicks (new tab etc.) follow the link.
	container.querySelectorAll('a[data-page]').forEach(link => {
		link.addEventListener('click', event => {
			if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
				return;
			}
			event.preventDefault();
			queueScrollToAlgoliaTop();
			refine(Number(link.dataset.page));
			link.blur();
		});
	});
});



/**
 * Global solutions tags HTML
 * @param  {Object} record   – any object that has record.global_solutions = [ "tag1", "tag2", … ]
 * @return {string}          – either a `<div class="post-card__tags">…</div>` or an empty string
 */
const renderGlobalSolutions = (record) => {
	if (!Array.isArray(record.global_solutions) || record.global_solutions.length === 0) {
		return '';
	}

	const globalSolutionTermSpans = record.global_solutions
		.map(term => `<span class="post-tags__tag">${term}</span>`)
		.join('');

	return `
    <div class="post-card__tags post-tags">
      ${globalSolutionTermSpans}
    </div>
  `;
};

// ---------------------------
// Render Initial Results
// ---------------------------
function renderInitialResults(input) {
	const container = document.getElementById('initial-results');
	container.innerHTML = '';

	const sections = Array.isArray(input)
		? input
		: Array.isArray(window.algoliaSearchBlockData.defaultSections) &&
		window.algoliaSearchBlockData.defaultSections.length
			? window.algoliaSearchBlockData.defaultSections
		: [
			{
				title: algoliaSearchLocalizedData.translations['sectionTitlePrimary'],
				paragraph:
					algoliaSearchLocalizedData.translations['sectionParagraphPrimary'],
				link: '#',
				linkText: algoliaSearchLocalizedData.translations['sectionLinkText'],
				items: input.results[0].hits,
			},
			{
				title: algoliaSearchLocalizedData.translations['sectionTitleSecondary'],
				paragraph:
					algoliaSearchLocalizedData.translations['sectionParagraphSecondary'],
				link: '#',
				linkText: algoliaSearchLocalizedData.translations['sectionLinkText'],
				items: input.results[1].hits,
			},
			{
				title: algoliaSearchLocalizedData.translations['sectionTitleTertiary'],
				paragraph:
					algoliaSearchLocalizedData.translations['sectionParagraphTertiary'],
				link: '#',
				linkText: algoliaSearchLocalizedData.translations['sectionLinkText'],
				items: input.results[2].hits,
			},
		];

	/**
	 * Newsletter logic
	 * @type {*|string}
	 */

	let newsletterLabel = algoliaSearchLocalizedData.translations['newsLetterLabel']
	let newsLetterTitle = algoliaSearchLocalizedData.translations['newsLetterTitle']

	const {
		link_to_default_view_newsletter,
		newsLetterLinkMaster,
		newsletterTextMaster
	} = window.algoliaSearchBlockData;

	function hasValidUrl(link) {
		return link && typeof link.url === 'string' && link.url.trim() !== '';
	}

	const staticNewsData = hasValidUrl(link_to_default_view_newsletter)
		? link_to_default_view_newsletter
		: hasValidUrl(newsLetterLinkMaster)
			? newsLetterLinkMaster
			: {};

	const {
		title: newsletterPreviewText   = algoliaSearchLocalizedData.translations['newsLetterPreview'],
		url:   newsletterPreviewUrl    = '#',
		target: newsletterPreviewTarget = ''
	} = staticNewsData;

	const newsletterLabelDefault = (
		newsletterTextMaster?.labelText?.trim()
			? newsletterTextMaster.labelText
			: algoliaSearchLocalizedData.translations['newsLetterLabel']
	);

	const newsLetterTitleDefault = (
		newsletterTextMaster?.headlineText?.trim()
			? newsletterTextMaster.headlineText
			: algoliaSearchLocalizedData.translations['newsLetterTitle']
	);


	const staticNewsletter = `
	<div class="era-algolia-newsletter-block has-background">
  <div class="era-algolia-newsletter-content">
    <div class="era-algolia-newsletter-text">
      <strong>${newsletterLabelDefault}</strong>
      <h4 class="wp-block-heading">
       ${newsLetterTitleDefault}
      </h4>
     <form class="newsletter-form">
<!--  <div class="newsletter-input-group">-->
<!--    <input type="email" placeholder="Enter your e-mail" required />-->
<!--    <div class="wp-block-button button-icon&#45;&#45;none is-style-icon button-size&#45;&#45;default">-->
<!--        <a class="wp-block-button__link has-black-color has-white-background-color has-text-color has-background has-link-color wp-element-button">-->
<!--            Subscribe-->
<!--        </a>-->
<!--    </div>-->
<!--  </div>-->
</form>
    </div>
    <div class="era-algolia-newsletter-image-container">
  <img loading="lazy" src="https://conscia.com/wp-content/uploads/2022/04/sd-wan-linkedin.jpg" alt="Cybersecurity illustration" />
  <div class="newsletter-image-button">
    <div class="wp-block-button button-icon--none button-size--default">
      <a href="${newsletterPreviewUrl}" ${newsletterPreviewTarget ? `target="${newsletterPreviewTarget}" rel="noopener noreferrer"` : ''} class="wp-block-button__link has-white-color has-electric-blue-background-color has-text-color has-background has-link-color wp-element-button">
        <span>${newsletterPreviewText}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
<path d="M1.83301 8.58708C1.83301 8.58708 3.83301 3.92041 8.49967 3.92041C13.1663 3.92041 15.1663 8.58708 15.1663 8.58708C15.1663 8.58708 13.1663 13.2537 8.49967 13.2537C3.83301 13.2537 1.83301 8.58708 1.83301 8.58708Z" stroke="#FEF2F2" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8.5 10.5869C9.60457 10.5869 10.5 9.69148 10.5 8.58691C10.5 7.48234 9.60457 6.58691 8.5 6.58691C7.39543 6.58691 6.5 7.48234 6.5 8.58691C6.5 9.69148 7.39543 10.5869 8.5 10.5869Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg> 
      </a>
    </div>
  </div>
</div>
  </div>
</div>
`;

	const newsletterBlock = staticNewsletter;

	/**
	 * Sections markup logic
	 */

	sections.forEach(({ title = '', paragraph = '', link = '', linkText = '', items }, index) => {
		if (items.length > 0) {
			const sectionEl = document.createElement('div');
			sectionEl.classList.add('era-algolia-section');

			sectionEl.innerHTML = `
			<header class="era-algolia-section-header">
				${title ? `<h2 class="wp-block-heading">${title}</h2>` : ''}
				<div class="era-algolia-section-header-inner">
					${paragraph ? `<p class="era-algolia-paragraph">${paragraph}</p>` : ''}
					${link ? `<div class="wp-block-button button-icon--none is-style-icon is-style-outline button-size--default view-all-button">
					<a href="${link}" class="wp-block-button__link wp-element-button">${linkText}</a></div>` : ''}
				</div>
			</header>
			<ul class="era-algolia-section-results">
				${items
				.map((item) => {
					const postTypeLabel =
						algoliaSearchLocalizedData.translations?.post_types?.[item.post_type]?.[algoliaSearchLocalizedData.currentLang] ||
						algoliaSearchLocalizedData.translations?.post_types?.[item.post_type]?.default ||
						item.post_type;

					// --- supergraphic logic (minimal + non-invasive) ---
					const hasFeatured = !!(item.featured_image && String(item.featured_image).trim());

					// Prefer precomputed fields from indexing (if you added them in PHP);
					// otherwise, map directly from the raw ACF values on the record.
					const styleClass = item.supergraphic_style_class || _mapStyleClass(item.post_settings_supergraphic_style);
					const bgClass    = item.supergraphic_background_class || _mapBgClass(item.post_settings_supergraphic_background);

					// If indexer provided a boolean, use it; else derive from the three fields.
					const hasSuperValues = (typeof item.supergraphic_has_values !== 'undefined')
						? !!item.supergraphic_has_values
						: !!(item.post_settings_choice) && !!styleClass && !!bgClass;

					// ONLY use supergraphic when NO featured image AND supergraphic is configured.
					const useSupergraphic = !hasFeatured && hasSuperValues;

					// Always keep the base class; append only when using supergraphic.
					const mediaClasses = `post-card__media${useSupergraphic ? ` has-no-media ${styleClass} ${bgClass}` : ''}`;

					return `
			<li class="era-algolia-hit">
				<a href="${item.url}" class="post-card">
					<div class="${mediaClasses}">
						${
						useSupergraphic
							? '' // no <img> so the ::after mask shows
							: `<img loading="lazy"
										src="${item.featured_image || fallbackImage || ''}"
										sizes="(max-width: 1024px) 100vw, 1024px"
										alt="${item.title}"
										class="post-card__img"
									/>`
					}
						${renderGlobalSolutions(item)}
					</div>
					<div class="post-meta">
						<span class="post-meta__post-type">${postTypeLabel}</span>
						${
						item.post_type === 'cases' || item.post_type === 'whitepapers' || item.post_type === 'page'
							? ''
							: `<span class="post-meta__published">
										${
								item.id
									? new Date(item.publish_date).toLocaleDateString('en-GB')
									: new Date(item.publish_date).toLocaleDateString()
							}
									</span>`
					}
					</div>
					<h6 class="post-card__title">${item.title}</h6>
					<p class="post-card__excerpt hidden">${item.excerpt}</p>
				</a>
			</li>
		`;
				})
				.join('')}

			</ul>
		`;

			container.appendChild(sectionEl);

			if (index === 2) {
				const newsletterEl = document.createElement('div');
				newsletterEl.innerHTML = newsletterBlock;
				container.appendChild(newsletterEl);
			}
		}
	});
}

// ---------------------------
// InstantSearch Interactive UI
// ---------------------------
search.addWidgets([
	searchBox({
		container: '#searchbox',
		placeholder: algoliaSearchLocalizedData.translations['searchPlaceholder'],
	}),
	clearRefinements({
		container: '#clear-all-refinements',
		templates: {
			resetLabel: algoliaSearchLocalizedData.translations['clearAllFilters'],
		},
	}),
	clearRefinements({
		container: '#clear-all-refinements-mobile',
		templates: {
			resetLabel: algoliaSearchLocalizedData.translations['clearAllFilters'],
		},
	}),
	stats({
		container: '#algolia-stats',
		templates: {
			text: ({ nbHits }) => {
				const searchResultsText = algoliaSearchLocalizedData.translations;
				const label =
					nbHits === 1
						? `${searchResultsText.searchStatsPrefixSingular || 'View'} <span>1</span> ${
							searchResultsText.searchStatsSingular || 'result'
						}`
						: `${searchResultsText.searchStatsPrefixPlural ||
						'View all'} <span>${nbHits}</span> ${
							searchResultsText.countLabelPlural || 'results'
						}`;

				return `<div class="wp-block-button">
							<a role="button" class="algolia-stats-button wp-block-button__link has-white-color has-black-background-color has-text-color has-background has-link-color wp-element-button">
								${label} 
								<span class="algolia-stats-button-svg-element">
									<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
										<path d="M3.83398 8.33325H13.1673" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
										<path d="M8.5 3.66656L13.1667 8.33323L8.5 12.9999" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
									</svg>
								</span>
							</a>
						</div>`;
			},
		},
	}),
	stats({
		container: '#algolia-stats-desktop-count',
		templates: {
			text: ({ nbHits }) => {
				const translationString = algoliaSearchLocalizedData.translations;
				// singular label: countLabelSingular → countLabel → “Result”
				const singular = translationString.countLabelSingular || translationString.countLabel || 'Result';
				// plural   label: countLabelPlural   → countLabel   → “Results”
				const plural   = translationString.countLabelPlural   || translationString.countLabel   || 'Results';
				const label    = nbHits === 1 ? singular : plural;
				return `${nbHits} ${label}`;
			},
		},
	}),
	hits({
		container: '#hits',
		templates: {
			item: (hit) => `
      <a href="${hit.url}" class="post-card">
        <div class="post-card__media${
				(
					(!hit.featured_image || String(hit.featured_image).trim() === '') &&
					(
						(typeof hit.supergraphic_has_values !== 'undefined')
							? !!hit.supergraphic_has_values
							: (!!hit.post_settings_choice && !!hit.supergraphic_style_class && !!hit.supergraphic_background_class)
					)
				)
					? ' has-no-media ' + (hit.supergraphic_style_class || '') + ' ' + (hit.supergraphic_background_class || '')
					: ''
			}">
          ${
				(
					(!hit.featured_image || String(hit.featured_image).trim() === '') &&
					(
						(typeof hit.supergraphic_has_values !== 'undefined')
							? !!hit.supergraphic_has_values
							: (!!hit.post_settings_choice && !!hit.supergraphic_style_class && !!hit.supergraphic_background_class)
					)
				)
					? ''  // supergraphic path → no <img>
					: `<img loading="lazy"
                    src="${hit.featured_image || fallbackImage || ''}"
                    sizes="(max-width: 1024px) 100vw, 1024px"
                    alt="${hit.title}"
                    class="post-card__img"
                  />`
			}
          ${renderGlobalSolutions(hit)}
        </div>
        <div class="post-meta">
          <span class="post-meta__post-type">${
				algoliaSearchLocalizedData.translations?.post_types?.[hit.post_type]?.[algoliaSearchLocalizedData.currentLang] ||
				algoliaSearchLocalizedData.translations?.post_types?.[hit.post_type]?.default ||
				hit.post_type
			}</span>
          ${
				hit.post_type === 'cases' || hit.post_type === 'whitepapers' || hit.post_type === 'page'
					? ''
					: `<span class="post-meta__published">
              ${hit.id ? new Date(hit.publish_date).toLocaleDateString('en-GB') : new Date(hit.publish_date).toLocaleDateString()}
          </span>`
			}
        </div>
        <h6 class="post-card__title">${hit.title}</h6>
        <p class="post-card__excerpt hidden">${hit.excerpt}</p>
      </a>
    `,
		},
	}),
	customPagination({
		container: document.querySelector('#pagination'),
		ariaControlsId: 'hits'
	}),
]);


// ------------------------------
// Switch View on User Interaction
// ------------------------------
let hasInteracted = false;

search.on('render', () => {
	const state = search.helper.state;
	const refinementSignature = getRefinementSignature(state);
	if (!hasRefinementSignature) {
		hasRefinementSignature = true;
		previousRefinementSignature = refinementSignature;
	} else if (refinementSignature !== previousRefinementSignature) {
		previousRefinementSignature = refinementSignature;
		queueScrollToAlgoliaTop('smooth');
	}

	if (pendingScrollToAlgoliaTop) {
		pendingScrollToAlgoliaTop = false;
		window.requestAnimationFrame(() => {
			flushQueuedScrollToAlgoliaTop();
		});
	}

	applyFacetShowMoreTranslationsInDom();

	const query = state.query.trim();
	const hasRefinements = Object.values(state.disjunctiveFacetsRefinements).some(
		(arr) => arr.length > 0
	);

	const shouldShowInitialView = !query && !hasRefinements;

	if (shouldShowInitialView) {
		if (hasInteracted) {
			document.getElementById('initial-results').style.display = 'block';
			document.getElementById('main-results').style.display = 'none';
			fetchInitialData();
			hasInteracted = false;
		}
	} else {
		if (!hasInteracted) {
			document.getElementById('initial-results').style.display = 'none';
			document.getElementById('main-results').style.display = 'block';
			hasInteracted = true;
		}
	}

	const queryFeedbackEl = document.querySelector(
		'#era-algolia-search-query-feedback > h3'
	);
	if (queryFeedbackEl) {
		const algoliaSearchQuery = search.helper.state.query.trim();
		const prefix = algoliaSearchLocalizedData.translations.searchFeedbackPrefix;
		queryFeedbackEl.textContent = algoliaSearchQuery
			? `${prefix} '${algoliaSearchQuery}'`
			: '';
	}

	let currentRefinements = search.helper.state.disjunctiveFacetsRefinements;
	let activeFilters = -1;
	for (const [key, values] of Object.entries(currentRefinements)) {
		activeFilters += values.length;
	}
	const activeFiltersElement = document.getElementById('activeFilters');
	if (activeFiltersElement) {
		activeFiltersElement.textContent = activeFilters > 0 ? `(${activeFilters})` : '';
	}

	/**
	 * Hide / Show pagination automatically
	 * Shows if 15 or more hits/items are visible
	 */

	const results = search.helper.lastResults;

	if (!results) {
		// No results yet: keep the server-rendered state.
	} else if (results.nbHits >= 15) {
		document.querySelector('#pagination').classList.add('show');
		document.querySelector('#pagination').classList.remove('hide');
	} else {
		document.querySelector('#pagination').classList.add('hide');
		document.querySelector('#pagination').classList.remove('show');
	}

	/**
	 * Keep opted-in counters outside this block in sync with the result set.
	 *
	 * Markup elsewhere on the page (e.g. an archive header rendered by the
	 * theme) can opt in with:
	 *
	 *   <span data-algolia-live-count
	 *         data-count-singular="{count} result"
	 *         data-count-plural="{count} results">1 result</span>
	 *
	 * The server-rendered text stays as the initial value, so the counter is
	 * still correct without JavaScript; it is replaced here with the count for
	 * the query that produced the hits actually on screen. The label templates
	 * come from the markup, so translations stay owned by whoever rendered it.
	 */
	if (results) {
		document.querySelectorAll('[data-algolia-live-count]').forEach((element) => {
			const template =
				results.nbHits === 1
					? element.dataset.countSingular
					: element.dataset.countPlural;

			if (template) {
				element.textContent = template.replace('{count}', results.nbHits);
			}
		});
	}

});

// ------------------------
// Init
// ------------------------
fetchInitialData();
search.start();

if (preselectedRefinements.length) {
	search.addWidget({
		init: (options) => {
			let state = options.helper.state;
			preselectedRefinements.forEach(([attribute, value]) => {
				// The facet must be declared on the helper before it can be
				// refined. Facets that already have a refinementList widget
				// are declared by that widget; taxonomy archives can preselect
				// an attribute without a visible facet, so declare it here.
				if (!state.isDisjunctiveFacet(attribute)) {
					state = state.addDisjunctiveFacet(attribute);
				}
				state = state.addDisjunctiveFacetRefinement(attribute, value);
			});
			// Refine on the state rather than through the helper, which would
			// reset the page that routing restored from /page/N/.
			options.helper.setState(state);
		},
	});
}

function facetHasPreSelectedRefinement(facet) {
	if (typeof algoliaSearchBlockData !== 'undefined') {
		let preselected = algoliaSearchBlockData['era_algolia_block_preselected_attributes'] || [];
		if (preselected.length) {
			for (let i = 0; i < preselected.length; i++) {
				const split = preselected[i].split(':');
				if (split[0] === facet) {
					return true;
				}
			}
		}
	}
	return false;
}

// Mobile filters logic…

/**
 * Mobile filters logic
 * TODO: Move to another file
 */

/* FACETS Mobile Filters Logic */

document.addEventListener('DOMContentLoaded', () => {
	const toggleButton = document.querySelector('.era-algolia-facet-toggle');
	const closeButton = document.getElementById('era-algolia-facet-close');
	const closeButtonSvg = document.querySelector('.era-algolia-facet-close > svg');
	const viewResultsButton = document.querySelector('.algolia-stats-button.mobile');
	const overlay = document.getElementById('post-type-overlay');
	const htmlElement = document.querySelector('html');
	const bodyElement = document.querySelector('body');

	if(toggleButton) {
		toggleButton.addEventListener('click', () => {
			// console.log( 'clicking' );
			overlay.classList.add('open');
			htmlElement.classList.add('no-scroll');
			bodyElement.classList.add('no-scroll');
		});
	}

	if(viewResultsButton) {
		viewResultsButton.addEventListener('click', () => {
			overlay.classList.remove('open');
			htmlElement.classList.remove('no-scroll');
			bodyElement.classList.remove('no-scroll');
		});
	}

	if (closeButton) {
		closeButton.addEventListener('click', () => {
			overlay.classList.remove('open');
			htmlElement.classList.remove('no-scroll');
			bodyElement.classList.remove('no-scroll');
		});
	}

	if(closeButtonSvg) {
		closeButtonSvg.addEventListener('click', () => {
			overlay.classList.remove('open');
			htmlElement.classList.remove('no-scroll');
			bodyElement.classList.remove('no-scroll');
		})
	}
});

document.addEventListener('change', (event) => {
	const target = event.target;
	if (!(target instanceof HTMLInputElement)) {
		return;
	}

	if (target.dataset.algoliaFacetCheckbox !== '1') {
		return;
	}

	const attribute = target.dataset.algoliaFacetAttribute;
	if (!attribute) {
		return;
	}

	lastSelectedFacetValueByAttribute[attribute] = String(target.value);
	queueScrollToAlgoliaTop();
	scrollAlgoliaBlockIntoView('smooth');
});

/**
 * Mobile overlay view results button logic
 */
document.addEventListener('DOMContentLoaded', () => {
	document.addEventListener('click', function (event) {
		const button = event.target.closest('.algolia-stats-button');
		if (!button) return;

		// Close the overlay
		const overlay = document.getElementById('post-type-overlay');
		if (overlay) overlay.classList.remove('open');

		// Scroll to the results
		// const results = document.getElementById('main-results');
		// if (results) {
		// 	results.scrollIntoView({behavior: 'smooth', block: 'start'});
		// }
	});
});

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('.era-algolia-facet-wrapper').forEach((wrapper) => {
		const toggleButton = wrapper.querySelector('.facet-toggle');
		if (!toggleButton) return;

		// Detect mobile wrappers
		const isMobile = wrapper.classList.contains('mobile');

		// Initialize state:
		// • mobile: collapsed + aria-expanded="false"
		// • desktop:    expanded + aria-expanded="true"
		wrapper.classList.toggle('is-collapsed', isMobile);
		toggleButton.setAttribute('aria-expanded', String(!isMobile));

		toggleButton.addEventListener('click', () => {
			const expanded = toggleButton.getAttribute('aria-expanded') === 'true';

			// Flip ARIA state
			toggleButton.setAttribute('aria-expanded', String(!expanded));
			// Toggle collapsed class
			wrapper.classList.toggle('is-collapsed', expanded);

			// Items inside
			const items = wrapper.querySelectorAll('.facet-item');

			if (!expanded) {
				// EXPANDING: staggered fade-in
				items.forEach((item, i) => {
					item.classList.remove('visible');
					setTimeout(() => item.classList.add('visible'), i * 50);
				});
			} else {
				// COLLAPSING: remove visibility
				items.forEach(item => item.classList.remove('visible'));
			}
		});
	});
});
